import { collection, query, where, getDocs, onSnapshot, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { FIREBASE_CONFIG } from '../constants';

// ---- Ürün kataloğu (productItems) — okuma maliyeti en aza indirilmiş ----
// Binlerce ürün var; her açılışta tamamını okumak binlerce okuma demek.
// Katalog cihazda (IndexedDB) saklanır. Açılışta yalnızca son eşitlemeden sonra değişen ürünler okunur
// (değişiklik yoksa 1 okuma). Oturum boyunca aynı sorgu canlı dinlenir, başka cihazdaki değişiklikler gelir.
//
// KURAL: productItems'a yazan her işlem updatedAt: serverTimestamp() koymalı (productWriteFields),
// silme yerine { deleted: true } işaretlenmeli (productDeleteFields). Aksi halde diğer cihazlar değişikliği görmez.

const CACHE_KEY = `productItems:v1:${FIREBASE_CONFIG.projectId}`;
const IDB_NAME = 'ayhome-cache';
const IDB_STORE = 'kv';

export const productWriteFields = () => ({ updatedAt: serverTimestamp() });
export const productDeleteFields = () => ({ deleted: true, updatedAt: serverTimestamp() });

// ---- IndexedDB (kullanılamazsa yalnızca bellek; davranış bozulmaz, sadece tasarruf olmaz) ----
const openIdb = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === 'undefined') return reject(new Error('IndexedDB yok'));
  const req = indexedDB.open(IDB_NAME, 1);
  req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const idbGet = async (key) => {
  const idb = await openIdb();
  return new Promise((resolve, reject) => {
    const req = idb.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

const idbSet = async (key, value) => {
  const idb = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// Timestamp → Date (IndexedDB Timestamp sınıfını saklayamaz)
const plain = (data) => {
  const out = {};
  Object.entries(data).forEach(([k, v]) => { out[k] = v instanceof Timestamp ? v.toDate() : v; });
  return out;
};

const millisOf = (v) => (v instanceof Date ? v.getTime() : 0);

// ---- Durum ----
let state = null; // { byId: { id: data }, lastSync: ms }
let list = null; // [{ id, ...data }] (silinmişler hariç)
let ready = null; // ilk yükleme promise'i
let unsubscribe = null;
let saveTimer = null;
const subscribers = new Set();

const rebuild = () => {
  list = Object.entries(state.byId)
    .filter(([, data]) => !data.deleted)
    .map(([id, data]) => ({ id, ...data }));
  subscribers.forEach(cb => cb(list));
};

const persist = () => {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    idbSet(CACHE_KEY, state).catch(err => console.warn('Ürün kataloğu cihaza kaydedilemedi:', err));
  }, 500);
};

// Yalnızca sunucuda kesinleşmiş zamanlar eşitleme noktası olur
const applyDocs = (docs) => {
  let changed = false;
  docs.forEach((d) => {
    if (d.metadata?.hasPendingWrites) return;
    const data = plain(d.data());
    state.byId[d.id] = data;
    state.lastSync = Math.max(state.lastSync, millisOf(data.updatedAt));
    changed = true;
  });
  return changed;
};

const startDeltaListener = () => {
  // >= : aynı milisaniyedeki değişiklik kaçmasın (sınırdaki birkaç belge yeniden okunur)
  const q = query(collection(db, 'productItems'), where('updatedAt', '>=', Timestamp.fromMillis(state.lastSync)));
  unsubscribe = onSnapshot(q, (snap) => {
    const changedDocs = snap.docChanges().filter(c => c.type !== 'removed').map(c => c.doc);
    if (applyDocs(changedDocs)) {
      rebuild();
      persist();
    }
  }, (err) => {
    console.error('Ürün kataloğu dinlenemedi:', err);
    unsubscribe = null;
  });
};

const init = async () => {
  let cached = null;
  try {
    cached = await idbGet(CACHE_KEY);
  } catch (err) {
    console.warn('Ürün kataloğu cihazdan okunamadı:', err);
  }

  if (cached?.byId) {
    state = cached;
  } else {
    // Bu cihazda ilk açılış: tamamı bir kez okunur
    const snap = await getDocs(collection(db, 'productItems'));
    state = { byId: {}, lastSync: 0 };
    applyDocs(snap.docs);
    persist();
  }
  rebuild();
  startDeltaListener();
};

const ensure = () => {
  if (!ready) {
    ready = init().catch((err) => {
      ready = null;
      throw err;
    });
  } else if (!unsubscribe && state) {
    startDeltaListener();
  }
  return ready;
};

// Ürün listesi: [{ id, ...data }] (silinmişler hariç)
export const getProductItems = async () => {
  await ensure();
  return list;
};

// Değişiklikleri dinle; dönen fonksiyon aboneliği bitirir
export const subscribeProductItems = (cb, onError) => {
  subscribers.add(cb);
  ensure().then(() => { if (subscribers.has(cb)) cb(list); }).catch(err => onError?.(err));
  return () => { subscribers.delete(cb); };
};

// Çıkışta dinleyiciyi kapat (cihazdaki katalog kalır; ürün listesi kullanıcıya özel değil)
export const stopProductCatalog = () => {
  unsubscribe?.();
  unsubscribe = null;
};
